import { ReactNode, useMemo } from 'react';
import { Role } from '../../types/user';
import { useQuery } from '@tanstack/react-query';
import { useIotsApi } from '../../contexts/iotsApi';
import useAuthUserInfo from '../../hooks/useAuthUserInfo';
import useQueryErrorNotifier from '../../hooks/useQueryErrorNotifier';
import CurrentUserContext, { CurrentUser } from '../../contexts/currentUser';

const CurrentUserProvider = ({
  children,
  loadingContent,
}: {
  children: ReactNode;
  loadingContent: ReactNode;
}) => {
  const { userId, hasRole } = useAuthUserInfo();
  const { getUserById, getUserOperator } = useIotsApi();
  const isOperator = hasRole(Role.OPERATOR);

  const {
    data: user = null,
    isFetching: isFetchingUser,
    error: userError,
  } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => getUserById(userId as string),
    enabled: userId !== null,
  });

  const {
    data: userOperator = null,
    isFetching: isFetchingUserOperator,
    error: userOperatorError,
  } = useQuery({
    queryKey: ['operatorUser', userId],
    queryFn: () => getUserOperator(),
    enabled: userId !== null && isOperator,
  });

  useQueryErrorNotifier([userError, userOperatorError]);

  const contextValue = useMemo<CurrentUser | null>(() => {
    if (!user || (isOperator && !userOperator)) {
      return null;
    }
    return {
      user,
      userOperator,
      isFetching: isFetchingUser || isFetchingUserOperator,
      isOperator,
      hasRole,
    };
  }, [
    user,
    isOperator,
    userOperator,
    isFetchingUser,
    isFetchingUserOperator,
    hasRole,
  ]);

  if ((!user && userError) || (!userOperator && userOperatorError)) {
    throw new Error('Logged in user information could not be retrieved');
  }

  if (!contextValue) {
    return loadingContent;
  }

  return (
    <CurrentUserContext.Provider value={contextValue}>
      {children}
    </CurrentUserContext.Provider>
  );
};

export default CurrentUserProvider;
